import { type KeyboardEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FieldValues, UseFormSetFocus } from 'react-hook-form';
import {
    GRID_TREE_DATA_GROUPING_FIELD,
    type GridCallbackDetails,
    type GridCellParams,
    type GridRowId,
    type GridRowModel,
    type MuiEvent
} from '../lib/dataGrid';

/**
 * Carries which DataGrid column the user double-clicked from a grid to the
 * record editor it opens. Transient router `location.state`, not a URL param,
 * so it survives neither reload nor sharing and the deep links stay clean.
 */
export interface EditorFocusState {
    focusField?: string;
}

/**
 * Grid side: an `onCellDoubleClick` handler that opens a row's editor, with the
 * double-clicked column name attached as {@link EditorFocusState}.
 *
 * It fires on the KEY COLUMN only — Fundzettelnummer, Zeichenblattnummer,
 * Tagebuchdatum, the Befund the tree groups by; everywhere else the pencil in
 * the actions column opens the record. That restriction is what leaves a
 * double-click on any other cell free to open THAT cell's editor
 * (`gridInlineEdit`); firing both would put the dialog on top of a row already
 * in edit mode. The key column keeps the dialog because it is the row's
 * identity.
 *
 * `editPathForRow` belongs to the grid: each computes its own edit route, may
 * branch on row type (the Befund tree) or return a falsy path to opt a row out.
 *
 * The same handler serves Enter on a selected key cell (see {@link openOnEnter}).
 */
export function useEditorCellFocus(
    /** The column that opens the record — the row's key. */
    keyField: string,
    editPathForRow: (row: GridRowModel) => string | null | undefined
) {
    const navigate = useNavigate();
    return (params: GridCellParams) => {
        // `editable` is checked as well as the field: should the key column ever
        // be edited in place, the cell's own editor wins and this stays out of
        // the way rather than opening a dialog over it.
        if (params.field !== keyField || params.colDef.editable) {
            return;
        }
        const path = editPathForRow(params.row);
        if (!path) {
            return;
        }
        const state: EditorFocusState = { focusField: params.field };
        navigate(path, { state });
    };
}

/**
 * Dialog side: focus the react-hook-form field named by a double-clicked grid
 * cell once the editor has opened.
 *
 * `aliasMap` maps a grid column `field` to the form field name where the two
 * differ. An unmapped or unknown name is a silent no-op (RHF `setFocus` on an
 * unregistered field does nothing), so action/derived columns need no case.
 *
 * The focus is deferred across two animation frames so it lands after the
 * form's `reset()` has seeded values AND after the MUI Dialog enter transition
 * and its focus-trap have settled — otherwise the trap steals focus back to the
 * paper. `shouldSelect` selects the text so typing replaces it.
 */
export function useFocusFieldOnOpen<TFieldValues extends FieldValues>(
    setFocus: UseFormSetFocus<TFieldValues>,
    open: boolean,
    focusField: string | undefined,
    aliasMap?: Record<string, string>
) {
    useEffect(() => {
        if (!open || !focusField) {
            return;
        }
        const name = aliasMap?.[focusField] ?? focusField;
        let innerFrame = 0;
        const outerFrame = requestAnimationFrame(() => {
            innerFrame = requestAnimationFrame(() => {
                try {
                    setFocus(name as never, { shouldSelect: true });
                } catch {
                    // Unknown field (derived/action column): nothing to focus.
                }
            });
        });
        return () => {
            cancelAnimationFrame(outerFrame);
            cancelAnimationFrame(innerFrame);
        };
    }, [open, focusField, aliasMap, setFocus]);
}

/**
 * An `onCellKeyDown` that opens a record on Enter — the keyboard's double-click,
 * through the grid's own double-click handler, so the key column and nothing
 * else opens a record.
 *
 * Only on the cell ITSELF in view mode: Enter in a cell editor commits it, and
 * on a link or button inside the cell it belongs to that control.
 *
 * In a tree grid Space expands a row and Enter opens it. MUI toggles the row on
 * Enter as well, and its tree listener ignores `defaultMuiPrevented`, so the
 * row's expansion is put back once the keystroke has been handled.
 */
export function openOnEnter<Params extends GridCellParams>(
    open: ((params: Params) => void) | undefined
) {
    return (
        params: Params,
        event: MuiEvent<KeyboardEvent<HTMLElement>>,
        details?: GridCallbackDetails
    ) => {
        if (
            !open ||
            event.key !== 'Enter' ||
            event.ctrlKey ||
            event.metaKey ||
            event.altKey ||
            event.shiftKey ||
            params.cellMode !== 'view' ||
            params.colDef.editable ||
            event.target !== event.currentTarget
        ) {
            return;
        }
        event.defaultMuiPrevented = true;
        const node = params.rowNode;
        if (
            params.field === GRID_TREE_DATA_GROUPING_FIELD &&
            node.type === 'group'
        ) {
            const expanded = Boolean(node.childrenExpanded);
            const api = details?.api as
                | {
                      setRowChildrenExpansion?: (
                          id: GridRowId,
                          isExpanded: boolean
                      ) => void;
                  }
                | undefined;
            // After every listener of this keystroke, the tree's included.
            queueMicrotask(() =>
                api?.setRowChildrenExpansion?.(params.id, expanded)
            );
        }
        open(params);
    };
}
